import pbfe from "./pbfe.js"
import editor from "./editor.js";
import toast from "./toast.js";
import privacyDialog from "./privacy-dialog.js";
import i18n, { _, t } from "./i18n.js";
import { LANG_URL_MAP, getLang, handleRouting, navigate } from "./index.js";
import createNavbar from "./navbar.js";

var container, box, urlDialog, linkDialog;

function init(_container, callback) {
    container = _container;
    privacyDialog.init(container);

    box = new pbfe.Flexbox("column");
    box.element.id = "welcomeScreen";
    box.justifyContent = "center";
    box.alignItems = "center";

    // Navbar
    var navbar = createNavbar();
    box.appendChild(navbar);

    var innerBox = new pbfe.Flexbox;
    innerBox.element.id = "welcomeInnerBox";
    innerBox.justifyContent = "center";
    innerBox.alignItems = "center";

    var titleBox = new pbfe.Flexbox("column");
    titleBox.element.id = "titleBox";
    titleBox.alignItems = "center";
    titleBox.gap = "1rem";
    
    var icon = document.createElement("img");
    icon.src = new URL('./icons/icon.svg', import.meta.url);
    icon.className = "app-icon";
    icon.alt = "Avatar Cropper Logo";
    icon.width = 64;
    icon.height = 64;

    var titleRow = new pbfe.Flexbox("row");
    titleRow.alignItems = "center";
    titleRow.justifyContent = "center";
    titleRow.gap = "0.75rem";
    titleRow.element.style.position = "relative";

    var appName = { element: document.createElement("h1") };
    appName.element.innerText = "Avatar Cropper";
    appName.element.id = "appName";

    titleRow.appendChild({ element: icon });
    titleRow.appendChild(appName);

    var versionBadge = new pbfe.Label("v" + process.env.npm_package_version);
    versionBadge.element.id = "versionBadge";
    titleRow.appendChild(versionBadge);

    titleBox.appendChild(titleRow);

    var appTagline = new pbfe.Label("");
    t(appTagline.element, "Quickly Crop Avatar for Discord, X (Twitter), etc.");
    appTagline.element.id = "appTagline";
    titleBox.appendChild(appTagline);

    var buttonsBox = new pbfe.Flexbox("column");
    buttonsBox.element.id = "openButtonsBox";
    buttonsBox.gap = "1rem";
    
    var openButton = new pbfe.Button("");
    t(openButton.element, "Open Image...");
    openButton.element.id = "btnOpenImage";
    buttonsBox.appendChild(openButton);

    var urlButton = new pbfe.Button("", "secondary");
    t(urlButton.element, "or Paste Image...");
    urlButton.element.id = "btnPasteImage";
    buttonsBox.appendChild(urlButton);

    var linkButton = new pbfe.Button("", "secondary");
    t(linkButton.element, "or From A Image URL");
    linkButton.element.id = "btnLinkImage";
    buttonsBox.appendChild(linkButton);

    var hint = new pbfe.Label("");
    t(hint.element, "or drag and drop an image here.");
    hint.element.id = "dragHint";
    buttonsBox.appendChild(hint);

    innerBox.appendChild(titleBox);
    innerBox.appendChild(buttonsBox);
    
    box.appendChild(innerBox);

    // Footer Section
    var footerBox = new pbfe.Flexbox("column");
    footerBox.element.id = "footerBox";
    footerBox.alignItems = "center";
    footerBox.gap = "1rem";

    var linksBox = new pbfe.Flexbox("row");
    linksBox.gap = "1.5rem";
    linksBox.alignItems = "center";

    var aboutBtn = new pbfe.Button("", "link");
    t(aboutBtn.element, "About");
    aboutBtn.element.classList.add("footer-link");
    linksBox.appendChild(aboutBtn);

    var privacyBtn = new pbfe.Button("", "link");
    t(privacyBtn.element, "Privacy");
    privacyBtn.element.classList.add("footer-link");
    linksBox.appendChild(privacyBtn);

    var contactBtn = new pbfe.Button("", "link");
    t(contactBtn.element, "Contact");
    contactBtn.element.classList.add("footer-link");
    linksBox.appendChild(contactBtn);

    footerBox.appendChild(linksBox);

    box.appendChild(footerBox);

    openButton.addEventListener("click", openFilePicker);
    initURLDialog();
    urlButton.addEventListener("click", urlDialog.show.bind(urlDialog));
    initLinkDialog();
    linkButton.addEventListener("click", linkDialog.show.bind(linkDialog));
    
    aboutBtn.addEventListener("click", () => {
        var lang = getLang();
        var prefix = LANG_URL_MAP[lang];
        var path = prefix ? "/" + prefix + "/about" : "/about";

        navigate(path);
    });

    privacyBtn.addEventListener("click", () => {
        var lang = getLang();
        var prefix = LANG_URL_MAP[lang];
        var path = prefix ? "/" + prefix + "/privacy" : "/privacy";

        navigate(path);
    });

    contactBtn.addEventListener("click", () => {
        var lang = getLang();
        var prefix = LANG_URL_MAP[lang];
        var path = prefix ? "/" + prefix + "/contact" : "/contact";

        navigate(path);
    });

    welcomeScreen.shown = false;
    if (callback) callback();
}

function initURLDialog() {
    urlDialog = new pbfe.Dialog("");
    t(urlDialog.title, "Paste Image");

    var hint = new pbfe.Label("");
    t(hint.element, "Paste your image into the input box.");
    hint.element.style.marginBottom = "0.5rem";
    urlDialog.appendChild(hint);

    var input = new pbfe.Input("text");
    input.element.id = "urlInput";
    input.element.setAttribute("aria-label", "Paste your image into the input box.");
    t(input.element, "Paste your image into the input box.", "aria-label");
    urlDialog.appendChild(input);

    urlDialog.appendHideButton("Cancel");

    input.addEventListener("paste", function(e) {
        var data = e.clipboardData || e.originalEvent.clipboardData;
        if (!data) return;

        var items = data.items;
        for (let i = 0; i < items.length; ++i) {
            let item = items[i];
            if (item.kind == "file") {
                let file = item.getAsFile();
                if (file.type.startsWith("image/")) {
                    editor.open(file, onEditorOpened);
                    break;
                }
            }
        }
    });

    input.addEventListener("input", function() {
        input.value = "";
    });

    container.appendChild(urlDialog);
}

function initLinkDialog() {
    linkDialog = new pbfe.Dialog("");
    t(linkDialog.title, "From A Image URL");

    var hint = new pbfe.Label("");
    t(hint.element, "Paste the image URL below.");
    hint.element.style.marginBottom = "0.5rem";
    linkDialog.appendChild(hint);

    var input = new pbfe.Input("text");
    input.element.id = "linkInput";
    input.element.placeholder = "https://";
    input.element.style.marginBottom = "1rem";
    linkDialog.appendChild(input);

    // Button Container
    var buttonBox = new pbfe.Flexbox("row");
    buttonBox.gap = "0.5rem";
    buttonBox.justifyContent = "flex-end";
    buttonBox.element.style.width = "100%";
    buttonBox.element.style.marginTop = "0.5rem";

    var cancelBtn = new pbfe.Button("", "secondary");
    t(cancelBtn.element, "Cancel");
    cancelBtn.element.style.minWidth = "80px";
    cancelBtn.addEventListener("click", function() {
        linkDialog.hide();
    });
    buttonBox.appendChild(cancelBtn);

    var goBtn = new pbfe.Button("");
    t(goBtn.element, "GO");
    goBtn.element.style.minWidth = "80px";
    
    // Allow Enter key to submit
    input.element.addEventListener("keypress", function(e) {
        if (e.key === "Enter") {
            goBtn.element.click();
        }
    });

    goBtn.addEventListener("click", async function() {
        var url = input.value.trim();
        if (!url) {
            toast.show(_("URL cannot be empty."));
            return;
        }

        var originalText = goBtn.text;
        goBtn.text = _("Loading...");
        goBtn.element.disabled = true;
        cancelBtn.element.disabled = true;

        await fetch_image_from_link(url, function(file) {
            editor.open(file, onEditorOpened);
        });
        
        goBtn.text = originalText;
        goBtn.element.disabled = false;
        cancelBtn.element.disabled = false;
    });
    buttonBox.appendChild(goBtn);

    linkDialog.appendChild(buttonBox);

    container.appendChild(linkDialog);
}

async function fetch_image_from_link(link, callback) {
    let link_regex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
    if (!link.match(link_regex)) {
        toast.show(_("Invalid URL format"));
        return;
    }

    try {
        const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(link)}`;

        let fetched_link = await fetch(proxyUrl).catch(e => {
            console.log(`Failed to fetch from ${link}!`, e);
            return null;
        });

        if (!fetched_link) {
            toast.show(_("Failed to fetch image. Network error."));
            return;
        }
        
        if (!fetched_link.ok) {
             toast.show(_("Failed to fetch image. Status: ") + fetched_link.status);
             return;
        }

        let blobbed_link = await fetched_link.blob();
        
        if (!blobbed_link.type.includes("image")) {
            toast.show(_("The URL does not point to a valid image."));
            return;
        }

        let ext = blobbed_link.type.split('/')[1] || 'png';
        
        callback(new File([blobbed_link], `avatarcropper.${ext}`, { type: blobbed_link.type }));

    } catch (e) {
        console.error(e);
        toast.show(_("An error occurred while fetching the image."));
    }
}

function onEditorOpened() {
    var lang = getLang();
    var prefix = LANG_URL_MAP[lang];
    var path = "/avatar-cropper-editor";
    if (prefix) path = "/" + prefix + path;

    history.pushState(null, "", path);
    handleRouting();
}

function show() {
    setTimeout(function() { box.element.classList.remove("hide"); }, 0);
    container.appendChild(box);
    container.appendChild(urlDialog);
    container.appendChild(linkDialog);
    welcomeScreen.shown = true;
}

function hide() {
    if (urlDialog) urlDialog.hide();
    if (linkDialog) linkDialog.hide();
    welcomeScreen.shown = false;

    if (container.contains(box)) {
        if (box.element.classList.contains("hide")) return;
        box.element.classList.add("hide");
        
        var currentBox = box;
        var onEnd = function(e) {
            if (e.target !== currentBox.element) return;
            currentBox.element.removeEventListener("transitionend", onEnd);
            
            if (!currentBox.element.classList.contains("hide")) return;
            if (container.contains(currentBox)) {
                container.removeChild(currentBox);
                if (urlDialog && container.contains(urlDialog)) container.removeChild(urlDialog);
                if (linkDialog && container.contains(linkDialog)) container.removeChild(linkDialog);
            }
        };
        currentBox.element.addEventListener("transitionend", onEnd);
    } else {
        if (urlDialog && container.contains(urlDialog)) container.removeChild(urlDialog);
        if (linkDialog && container.contains(linkDialog)) container.removeChild(linkDialog);
    }
}

var inputEl = document.createElement("input");
inputEl.id = "filePickerInput";
inputEl.type = "file";
inputEl.accept = "image/*";

function openFilePicker() {
    inputEl.value = null;
    inputEl.click();
}

inputEl.addEventListener("change", function(e) {
    var fileList = this.files;
    for (let i = 0; i < fileList.length; ++i) {
        let file = fileList[i];
        if (file.type.startsWith("image/")) {
            editor.open(file, onEditorOpened);
            return;
        }
    }
    toast.show(_("Invalid image file."));
});

var welcomeScreen = {
    shown: false, init, show, hide
}
export default welcomeScreen;